"use client";

import * as React from "react";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import Logo from "@/components/logo/logo";
import { SignupRequiredModal } from "@/components/auth/signup-required-modal";
import { useAuthModal } from "@/contexts/auth-modal-context";
import { MessageNotificationBadge } from "@/components/messaging/MessageNotificationBadge";
import { NotificationBadge } from "@/components/notifications/NotificationBadge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  addRecentSearch,
  isPropertyId,
  extractPropertyId,
} from "@/lib/recent-searches";
import { Search, X, Sparkles, Loader2, Bug } from "lucide-react";
import { cn } from "@/lib/utils";
import { BugReportDialog } from "@/components/bug-report/bug-report-dialog";
import { RecentSearchesDropdown } from "@/components/search/search-support/recent-searches-dropdown";

/**
 * Header Component - Simplified responsive navigation
 *
 * Mobile: Logo + Theme + User (navigation handled by MobileBottomNav)
 * Desktop: Logo + search + actions + user menu
 *
 * The mobile hamburger menu has been removed in favor of the
 * MobileBottomNav component which provides a cleaner, thumb-friendly
 * navigation experience following iOS/Android patterns.
 *
 * @author Derrick J. Albers
 */
const Header = () => {
  const { data: session, status } = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const { openAuthModal } = useAuthModal();
  const [isSearching, setIsSearching] = React.useState(false);
  const [showRecentSearches, setShowRecentSearches] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [showHeaderSearch, setShowHeaderSearch] = React.useState(false);
  const [showSignupModal, setShowSignupModal] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Hide header search on homepage until scrolling past hero section (650px)
  const isHomePage = pathname === "/" || pathname === "/home";

  React.useEffect(() => {
    const handleScroll = () => {
      if (isHomePage) {
        // Show header search after scrolling 650px (when hero section scrolls out)
        const scrolled = window.scrollY > 650;
        setShowHeaderSearch(scrolled);
      } else {
        // Always show search on other pages (including search page)
        setShowHeaderSearch(true);
      }
    };

    // Initial check
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    // Show signup modal if not authenticated
    if (status !== "authenticated") {
      setShowSignupModal(true);
      return;
    }

    setIsSearching(true);
    setShowRecentSearches(false);

    try {
      // Navigate directly to listing page
      if (isPropertyId(searchQuery)) {
        const propertyId = extractPropertyId(searchQuery);
        if (propertyId) {
          window.location.href = `/listing/${propertyId}`;
          return;
        }
      }

      addRecentSearch(searchQuery);
      window.location.href = `/search?q=${encodeURIComponent(searchQuery.trim())}`;
    } catch (error) {
      console.error("Search navigation error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectRecentSearch = (query: string) => {
    setShowRecentSearches(false);
    setSearchQuery(query);
    handleSearchWithQuery(query);
  };

  const handleSearchWithQuery = async (query: string) => {
    if (!query.trim()) return;

    // Show signup modal if not authenticated
    if (status !== "authenticated") {
      setShowSignupModal(true);
      return;
    }

    setIsSearching(true);
    setShowRecentSearches(false);

    if (isPropertyId(query)) {
      const propertyId = extractPropertyId(query);
      if (propertyId) {
        window.location.href = `/listing/${propertyId}`;
        return;
      }
    }

    addRecentSearch(query);
    window.location.href = `/search?q=${encodeURIComponent(query.trim())}`;
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
    if (e.key === "Escape") {
      setShowRecentSearches(false);
    }
  };

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // If already on home page, scroll to top instead of navigating
    if (pathname === "/" || pathname === "/home") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      {/* <div className="flex h-full w-full items-center gap-2 sm:gap-4"> */}
      <div className="flex h-full w-full items-center justify-between gap-2 overflow-x-hidden overflow-y-hidden  sm:gap-4">
        {/* Logo - Always visible, different sizes for mobile/desktop */}
        <Link
          href="/"
          className="shrink-0"
          data-tour="mobile-header-logo"
          onClick={handleHomeClick}
        >
          <div className="hidden lg:block">
            <Logo size="lg" variant="pulse" />
          </div>
          <div className="lg:hidden">
            <Logo size="md" variant="pulse" />
          </div>
        </Link>

        {/* Search Bar - Fills available space */}
        {showHeaderSearch && (
          <Popover
            open={showRecentSearches}
            onOpenChange={setShowRecentSearches}
          >
            <PopoverTrigger asChild>
              <div
                data-tour="global-search"
                className="hidden max-w-md flex-1 sm:block"
                onClick={() =>
                  !searchQuery.trim() && setShowRecentSearches(true)
                }
              >
                {/* Minimal Clean Search Input */}
                <div className="relative flex items-center">
                  <div
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2.5 py-1",
                      "bg-usland-blue-light/30",
                      "border border-white/10",
                      "hover:border-cyan-500/30",
                      "transition-all duration-150",
                    )}
                  >
                    {/* Search Icon */}
                    <Search className="h-3.5 w-3.5 flex-shrink-0 text-white/50 " />

                    {/* Single-line Input */}
                    <input
                      type="text"
                      ref={inputRef}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onFocus={() => {
                        if (!searchQuery.trim()) setShowRecentSearches(true);
                      }}
                      placeholder="Search..."
                      className="bg-usland-slate-700 min-w-0 flex-1 border-0 text-xs text-white outline-none placeholder:text-white/30"
                    />

                    {/* Clear Button */}
                    {searchQuery && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearSearch();
                        }}
                        className="bg-usland-blue/40 flex-shrink-0 p-0.5 text-white/40 transition-colors hover:text-white"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </PopoverTrigger>
            <PopoverContent
              className="w-[360px] p-0"
              align="start"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <RecentSearchesDropdown
                onSelectSearch={handleSelectRecentSearch}
              />
            </PopoverContent>
          </Popover>
        )}

        {/* Actions - Icons on the right */}
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {/* Notifications - Visible on all screen sizes */}
          {status === "authenticated" && (
            <>
              <NotificationBadge />
              <MessageNotificationBadge />
            </>
          )}

          {/* Bug Report Button - BETA */}
          <BugReportDialog
            trigger={
              <Button
                variant="ghost"
                size="icon"
                data-tour="bug-report"
                className={
                  cn()
                  // "relative h-11 w-11",
                  // "sidebar-hover hover:bg-accent/50",
                  // "transition-all duration-300",
                }
                aria-label="Report a bug"
              >
                <Bug className="h-5 w-5 text-usland-red-dark transition-colors hover:text-[hsl(var(--icon-header-hover))]" />
                {/* Beta badge indicator */}
                {/* <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-gradient-to-r from-red-500 to-orange-500"></span>
                </span> */}
              </Button>
            }
          />

          {/* <ThemeChanger /> */}

          {/* Show Log In/Register buttons if not logged in - Hidden on mobile */}
          {status === "unauthenticated" && (
            <>
              <Button
                variant="default"
                size="sm"
                onClick={() => openAuthModal("signin")}
                className="usland-hero-button"
              >
                Log In
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => openAuthModal("signup")}
                className="usland-hero-button"
              >
                Register
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Signup Required Modal for Unauthenticated Search */}
      <SignupRequiredModal
        isOpen={showSignupModal}
        onClose={() => setShowSignupModal(false)}
      />
    </>
  );
};

export default Header;
